import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Storage } from '@ionic/storage-angular';
import { LocalSettings } from 'src/app/model';
import { MapType } from '@capacitor/google-maps';
import { isInitThemeDark, setAppTheme } from '../app.utils';
import { MarkersType } from '../types/markers.types';

@Injectable({
  providedIn: 'root',
})
export class LocaLstorageService {
  private _storage: Storage | null = null;

  constructor(
    private storage: Storage,
  ) { }

  private readonly MAPTYPE = new BehaviorSubject<MapType>(MapType.Normal);
  readonly mapType$ = this.MAPTYPE.asObservable();
  private readonly MARKERSTYPE = new BehaviorSubject<MarkersType>(MarkersType.Install);
  readonly markersType$ = this.MARKERSTYPE.asObservable();

  private readonly AUTOCALIBRATION = new BehaviorSubject<boolean>(true);
  readonly autoCalibration$ = this.AUTOCALIBRATION.asObservable();

  private readonly CUSTOMIDGENERATION = new BehaviorSubject<boolean>(false);
  readonly customIdGeneration$ = this.CUSTOMIDGENERATION.asObservable();

  private readonly DARKMODE = new BehaviorSubject<boolean>(false);
  readonly darkMode$ = this.DARKMODE.asObservable();

  private readonly INTERVALPLACING = new BehaviorSubject<boolean>(false);
  readonly intervalPlacing$ = this.INTERVALPLACING.asObservable();

  private readonly INTERVALMETERS = new BehaviorSubject<number>(3);
  readonly intervalMeters$ = this.INTERVALMETERS.asObservable();

  private readonly SNAPTOROAD = new BehaviorSubject<boolean>(false);
  readonly snapToRoad$ = this.SNAPTOROAD.asObservable();

  private readonly MOREFILTERSEXPANDED = new BehaviorSubject<boolean>(false);
  readonly moreFiltersExpanded$ = this.MOREFILTERSEXPANDED.asObservable();

  private readonly SELECTEDOWNER = new BehaviorSubject<number | undefined>(undefined);
  readonly selectedOwner$ = this.SELECTEDOWNER.asObservable();

  private readonly SELECTEDPROJECT = new BehaviorSubject<number | undefined>(undefined);
  readonly selectedProject$ = this.SELECTEDPROJECT.asObservable();

  /* INIT START */
  public async init() {
    this._storage = await this.storage.create();
    const autoCalibration = await this._storage?.get(LocalSettings.AutoCalibration);

    this.autoCalibration = typeof autoCalibration === 'boolean' ? autoCalibration : true;
    const mapType = await this._storage?.get(LocalSettings.MapType);
    this.mapType = mapType === MapType.Normal || mapType === MapType.Hybrid ? mapType : MapType.Normal;
    // 'mount' is what Install was called before the MAIN-4095 rename. Without this,
    // an upgraded install reads an unknown value and every Install-only control hides.
    const markersType = await this._storage?.get(LocalSettings.MarkersType);
    this.markersType = markersType === MarkersType.Replace ? MarkersType.Replace : MarkersType.Install;
    this.customIdGeneration = false;
    this.darkMode = await this.initThemeValue();
    this.intervalMeters = (await this._storage?.get(LocalSettings.IntervalMeters)) || 3;
    this.intervalPlacing = !!(await this._storage?.get(LocalSettings.IntervalPlacing));
    this.snapToRoad = !!(await this._storage?.get(LocalSettings.SnapToRoad));
    // Default collapsed — the whole point is that most users never open it.
    this.moreFiltersExpanded = !!(await this._storage?.get(LocalSettings.MoreFiltersExpanded));
  }

  public async initProject() {
    this.selectedOwner = (await this._storage?.get(LocalSettings.SelectedOwner)) || undefined;
    this.selectedProject = (await this._storage?.get(LocalSettings.SelectedProject)) || undefined;
  }

  public clear = () => {
    this._storage?.clear();

    // The default is on, the same one init() applies to a fresh install.
    // Writing false here turned the feature off at every sign-out, which is
    // what the MAIN-2116 notice existed to undo and explain.
    this.autoCalibration = true;
    this.mapType = MapType.Normal;
    this.customIdGeneration = false;
    this.darkMode = false;
    this.intervalMeters = 3;
    this.intervalPlacing = false;
    this.snapToRoad = false;
  }

  /* AUTOCALIBRATION */
  get autoCalibration(): boolean {
    return this.AUTOCALIBRATION.getValue();
  }
  set autoCalibration(val: boolean) {
    this._storage?.set(LocalSettings.AutoCalibration, val);
    this.AUTOCALIBRATION.next(val);
  }

  /* MAPTYPE */
  get mapType(): MapType {
    return this.MAPTYPE.getValue();
  }
  set mapType(val: MapType) {
    this._storage?.set(LocalSettings.MapType, val);
    this.MAPTYPE.next(val);
  }

  /* MARKERSTYPE */
  get markersType(): MarkersType {
    return this.MARKERSTYPE.getValue();
  }
  set markersType(val: MarkersType) {
    this._storage?.set(LocalSettings.MarkersType, val);
    this.MARKERSTYPE.next(val);
  }

  /* CUSTOMIDGENERATION */
  get customIdGeneration(): boolean {
    return this.CUSTOMIDGENERATION.getValue();
  }
  set customIdGeneration(val: boolean) {
    this._storage?.set(LocalSettings.CustomIdGeneration, val);
    this.CUSTOMIDGENERATION.next(val);
  }

  /* DARKMODE */
  get darkMode(): boolean {
    return this.DARKMODE.getValue();
  }
  set darkMode(val: boolean) {
    this._storage?.set(LocalSettings.DarkMode, val);
    this.DARKMODE.next(val);

    setAppTheme(val ? 'dark' : 'light');
  }

  private async initThemeValue(): Promise<boolean> {
    const localValue = await this._storage?.get(LocalSettings.DarkMode);

    if (typeof localValue === 'boolean') {
      return localValue;
    }

    return isInitThemeDark();
  }

  /* INTERVALPLACING */
  get intervalPlacing(): boolean {
    return this.INTERVALPLACING.getValue();
  }
  set intervalPlacing(val: boolean) {
    this.INTERVALPLACING.next(val);
    this._storage?.set(LocalSettings.IntervalPlacing, val);
  }

  /* INTERVALMETERS */
  get intervalMeters(): number {
    return this.INTERVALMETERS.getValue();
  }
  set intervalMeters(val: number) {
    const valNum = Math.abs(Number(val)) || 1;
    this._storage?.set(LocalSettings.IntervalMeters, valNum);
    this.INTERVALMETERS.next(valNum);
  }

  /* SNAPTOROAD */
  get snapToRoad(): boolean {
    return this.SNAPTOROAD.getValue();
  }
  set snapToRoad(val: boolean) {
    this._storage?.set(LocalSettings.SnapToRoad, val);
    this.SNAPTOROAD.next(val);
  }

  /* MOREFILTERSEXPANDED */
  get moreFiltersExpanded(): boolean {
    return this.MOREFILTERSEXPANDED.getValue();
  }
  set moreFiltersExpanded(val: boolean) {
    this._storage?.set(LocalSettings.MoreFiltersExpanded, val);
    this.MOREFILTERSEXPANDED.next(val);
  }

  /* SELECTEDOWNER */
  get selectedOwner(): number | undefined {
    return this.SELECTEDOWNER.getValue();
  }
  set selectedOwner(id: number | undefined) {
    this._storage?.set(LocalSettings.SelectedOwner, id);
    this.SELECTEDOWNER.next(id);
  }

  /* SELECTEDPROJECT */
  get selectedProject(): number | undefined {
    return this.SELECTEDPROJECT.getValue();
  }
  set selectedProject(id: number | undefined) {
    this._storage?.set(LocalSettings.SelectedProject, id);
    this.SELECTEDPROJECT.next(id);
  }
}
